import type { ReviewFormData, ReviewFormMode, ValidationErrors } from '../../model/review/interfaces';
import {
  REVIEW_AGE_GROUPS,
  REVIEW_GENDERS,
  type ReviewAgeGroup,
  type ReviewGender,
} from '../../model/review/review';

type ReviewFormState = ReviewFormData;

// Validation rules
export const VALIDATION_RULES = {
  NICKNAME_MIN_LENGTH: 4,
  NICKNAME_MAX_LENGTH: 25,
  TITLE_MAX_LENGTH: 200,
  CONTENT_MAX_LENGTH: 5000,
  MAX_IMAGES: 5,
} as const;

// Validation functions
export const isValidNickname = (nickname: string): boolean => {
  const trimmed = nickname.trim();
  return (
    trimmed.length >= VALIDATION_RULES.NICKNAME_MIN_LENGTH && trimmed.length <= VALIDATION_RULES.NICKNAME_MAX_LENGTH
  );
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return email.trim().length > 0 && emailRegex.test(email.trim());
};

export const isValidRating = (rating: number): boolean => {
  return rating > 0 && rating <= 5;
};

export const isValidTitle = (title: string): boolean => {
  return title.trim().length > 0 && title.length <= VALIDATION_RULES.TITLE_MAX_LENGTH;
};

export const isValidContent = (content: string): boolean => {
  return content.trim().length > 0 && content.length <= VALIDATION_RULES.CONTENT_MAX_LENGTH;
};

/** The cap covers the whole review, so an edit keeping five attachments has no room for a sixth. */
export const isValidImageCount = (images: File[], keptMediaCount: number = 0): boolean => {
  return images.length + keptMediaCount <= VALIDATION_RULES.MAX_IMAGES;
};

/** The form offers yes and no; the endpoint also accepts unsure from other API clients. */
export const REVIEW_RECOMMEND_ANSWERS = ['yes', 'no', 'unsure'] as const;

export const isValidRecommend = (recommend: string): boolean => {
  return (REVIEW_RECOMMEND_ANSWERS as readonly string[]).includes(recommend);
};

export const isValidAgeGroup = (ageGroup: string): boolean => {
  return REVIEW_AGE_GROUPS.includes(ageGroup as ReviewAgeGroup);
};

export const isValidGender = (gender: string): boolean => {
  return REVIEW_GENDERS.includes(gender as ReviewGender);
};

/**
 * The two skin fields are checked for an answer rather than against the taxonomy: their options are
 * the product taxonomy the server hands back at runtime, so a list hardcoded here would start
 * refusing values the form itself offered the moment the taxonomy changed.
 */
export const isValidSkinType = (skinType: string): boolean => {
  return skinType.trim().length > 0;
};

export const isValidSkinConcern = (skinConcern: string): boolean => {
  return skinConcern.trim().length > 0;
};

// Combined validation for form submission
// Members submit with their account name/email, which the form shows read-only and fills from the
// account, so those fields must not block submission (e.g. an account display name shorter than
// the guest minimum).
export const isFormValid = (formState: ReviewFormState, isMember: boolean = false): boolean => {
  const isIdentityValid = isMember || (isValidNickname(formState.nickname) && isValidEmail(formState.email));

  return (
    isValidRating(formState.rating) &&
    isValidTitle(formState.title) &&
    isValidContent(formState.content) &&
    isIdentityValid &&
    isValidRecommend(formState.recommend) &&
    isValidSkinType(formState.skinType) &&
    isValidSkinConcern(formState.skinConcern) &&
    isValidAgeGroup(formState.ageGroup) &&
    isValidGender(formState.gender) &&
    isValidImageCount(formState.images, formState.keptMedia.length)
  );
};

/**
 * The update endpoint takes only rating, text and media; requiring more would disable Submit.
 * The skin fields stay out for a second reason: reviews written before they were required hold empty
 * values, and demanding one on edit would strand those authors behind a field they never filled.
 */
export const isEditFormValid = (formState: ReviewFormState): boolean => {
  return (
    isValidRating(formState.rating) &&
    isValidTitle(formState.title) &&
    isValidContent(formState.content) &&
    isValidImageCount(formState.images, formState.keptMedia.length)
  );
};

// Get validation errors

/** The fields `isEditFormValid` holds an edit to, which a new review is held to as well. */
const getEditableFieldErrors = (formState: ReviewFormState): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (!isValidRating(formState.rating)) {
    errors.rating = 'Please select a rating';
  }

  if (!isValidTitle(formState.title)) {
    if (formState.title.trim().length === 0) {
      errors.title = 'Title is required';
    } else {
      errors.title = `Title must be ${VALIDATION_RULES.TITLE_MAX_LENGTH} characters or less`;
    }
  }

  if (!isValidContent(formState.content)) {
    if (formState.content.trim().length === 0) {
      errors.content = 'Review content is required';
    } else {
      errors.content = `Review must be ${VALIDATION_RULES.CONTENT_MAX_LENGTH} characters or less`;
    }
  }

  if (!isValidImageCount(formState.images, formState.keptMedia.length)) {
    errors.images = `Maximum ${VALIDATION_RULES.MAX_IMAGES} images allowed`;
  }

  return errors;
};

/**
 * Why the submit was refused, in the reviewer's words. Takes the same `isMember` and `mode` as the
 * two predicates above because it has to report exactly what they block on: naming a field an edit
 * never sends, or a member's account identity, would point at a control the form is happy without.
 */
export const getValidationErrors = (
  formState: ReviewFormState,
  isMember: boolean = false,
  mode: ReviewFormMode = 'create',
): ValidationErrors => {
  const errors = getEditableFieldErrors(formState);

  if (mode === 'edit') {
    return errors;
  }

  if (!isMember) {
    if (!isValidNickname(formState.nickname)) {
      if (formState.nickname.trim().length === 0) {
        errors.nickname = 'Nickname is required';
      } else if (formState.nickname.trim().length < VALIDATION_RULES.NICKNAME_MIN_LENGTH) {
        errors.nickname = `Nickname must be at least ${VALIDATION_RULES.NICKNAME_MIN_LENGTH} characters`;
      } else {
        errors.nickname = `Nickname must be ${VALIDATION_RULES.NICKNAME_MAX_LENGTH} characters or less`;
      }
    }

    if (!isValidEmail(formState.email)) {
      if (formState.email.trim().length === 0) {
        errors.email = 'Email is required';
      } else {
        errors.email = 'Please enter a valid email address';
      }
    }
  }

  if (!isValidRecommend(formState.recommend)) {
    errors.recommend = 'Please tell us whether you would buy this again';
  }

  if (!isValidSkinType(formState.skinType)) {
    errors.skinType = 'Please select your skin type';
  }

  if (!isValidSkinConcern(formState.skinConcern)) {
    errors.skinConcern = 'Please select your skin concern';
  }

  if (!isValidAgeGroup(formState.ageGroup)) {
    errors.ageGroup = 'Please select your age group';
  }

  if (!isValidGender(formState.gender)) {
    errors.gender = 'Please select your gender';
  }

  return errors;
};
